import { PaletteMode } from "@mui/material";
import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  CompletionStatus,
  CountriesList,
  EmployeeStatus,
  LanguageExperience,
  MaritalStatus,
  MedicalStatus,
  Nationalities,
  ProfessionalTypes,
  ReligionTypes,
} from "../process/types/processTypes";

export type PreferencesState = {
  isDesktop: boolean;
  isDrawerOpen: boolean;
  isMobileDrawerOpen: boolean;
  isFilterOpen: boolean;
  isMiniMode: boolean;
  pageTitle: string;
  mode: PaletteMode;
  lang: string;
  locale: string;
  searchText: string | undefined;
  status: EmployeeStatus | undefined;
  nationality: Nationalities | undefined;
  appliedCountry: CountriesList | undefined;
  addressRegion: string | undefined;
  brokerName: string | undefined;
  maritalStatus: MaritalStatus | undefined;
  religion: ReligionTypes | undefined;
  profession: ProfessionalTypes | undefined;
  arabic: LanguageExperience | undefined;
  amharic: LanguageExperience | undefined;
  english: LanguageExperience | undefined;
  ageStart: number | undefined;
  ageEnd: number | undefined;
  experienceYears: number | undefined;
  agencyId: string | undefined;
  agentId: string | undefined;
  cocCompleted: CompletionStatus | undefined;
  medicalStatus: MedicalStatus | undefined;
  biometricsCompleted: CompletionStatus | undefined;
  visaIssued: string | undefined;
  labourIssued: string | undefined;
  flightBooked: string | undefined;
};

const initialState: PreferencesState = {
  isDesktop: true,
  isDrawerOpen: false,
  isMiniMode: false,
  isMobileDrawerOpen: false,
  isFilterOpen: false,
  pageTitle: "Dashboard",
  mode: "light",
  lang: "en",
  locale: "en",
  searchText: "",
  status: undefined,
  nationality: undefined,
  appliedCountry: undefined,
  addressRegion: undefined,
  brokerName: undefined,
  maritalStatus: undefined,
  religion: undefined,
  profession: undefined,
  arabic: undefined,
  amharic: undefined,
  english: undefined,
  ageStart: undefined,
  ageEnd: undefined,
  experienceYears: undefined,
  agencyId: undefined,
  agentId: undefined,
  cocCompleted: undefined,
  medicalStatus: undefined,
  biometricsCompleted: undefined,
  visaIssued: undefined,
  labourIssued: undefined,
  flightBooked: undefined,
};

export const preferencesSlice = createSlice({
  name: "preferences",
  initialState,
  reducers: {
    toggleThis: (state, { payload }) => {
      switch (payload.type) {
        case "Drawer":
          state.isDrawerOpen = payload.newValue;
          break;
        case "Mini":
          state.isMiniMode = payload.newValue;
          localStorage.setItem("miniMode", payload.newValue);
          break;
        case "Mobile":
          state.isMobileDrawerOpen = payload.newValue;
          break;
        case "Filter":
          state.isFilterOpen = payload.newValue;
          // Close the nav sidebar when filter opens to avoid overlap
          if (payload.newValue) state.isDrawerOpen = false;
          break;
      }
    },

    setSearchText: (state, { payload }) => {
      state.searchText = payload;
    },
    setStatus: (state, { payload }) => {
      state.status = payload;
    },
    setNationality: (state, { payload }) => {
      state.nationality = payload;
    },
    setAppliedCountry: (state, { payload }) => {
      state.appliedCountry = payload;
    },
    setAddressRegion: (state, { payload }) => {
      state.addressRegion = payload;
    },
    setBrokerName: (state, { payload }) => {
      state.brokerName = payload;
    },
    setMaritalStatus: (state, { payload }) => {
      state.maritalStatus = payload;
    },
    setReligion: (state, { payload }) => {
      state.religion = payload;
    },
    setProfession: (state, { payload }) => {
      state.profession = payload;
    },
    setArabic: (state, { payload }) => {
      state.arabic = payload;
    },
    setAmharic: (state, { payload }) => {
      state.amharic = payload;
    },
    setEnglish: (state, { payload }) => {
      state.english = payload;
    },
    setAgeStart: (state, { payload }) => {
      state.ageStart = payload;
    },
    setAgeEnd: (state, { payload }) => {
      state.ageEnd = payload;
    },
    setExperienceYears: (state, { payload }) => {
      state.experienceYears = payload;
    },
    setLocalAgency: (state, { payload }) => {
      state.agencyId = payload;
    },
    setForeignAgent: (state, { payload }) => {
      state.agentId = payload;
    },
    setCocCompleted: (state, { payload }) => {
      state.cocCompleted = payload;
    },
    setMedicalStatus: (state, { payload }) => {
      state.medicalStatus = payload;
    },
    setBiometricsCompleted: (state, { payload }) => {
      state.biometricsCompleted = payload;
    },

    setMode: (state, { payload }) => {
      state.mode = payload;
      localStorage.setItem("mode", payload);
    },
    setLanguage: (state, { payload }) => {
      state.lang = payload;
      state.locale =
        payload === "ar" ? "ar-sa" : payload === "et" ? "am" : payload;
      localStorage.setItem("lang", payload);
    },

    changePageTitle: (state, { payload }) => {
      state.pageTitle = payload;
    },
  },
  extraReducers: {},
});

export const {
  toggleThis,
  changePageTitle,
  setSearchText,
  setStatus,
  setNationality,
  setAppliedCountry,
  setAddressRegion,
  setBrokerName,
  setMaritalStatus,
  setReligion,
  setProfession,
  setArabic,
  setAmharic,
  setEnglish,
  setAgeStart,
  setAgeEnd,
  setExperienceYears,
  setLocalAgency,
  setForeignAgent,
  setCocCompleted,
  setMedicalStatus,
  setBiometricsCompleted,
  setMode,
  setLanguage,
} = preferencesSlice.actions;

export const selectPreference = (state: RootState) =>
  state.preferences as PreferencesState;

export default preferencesSlice.reducer;
